import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuth } from './AuthContext';

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  heading: number;
  speed: number;
}

interface GPSContextType {
  isTracking: boolean;
  currentLocation: LocationData | null;
  currentLoadId: string | null;
  permissionGranted: boolean;
  error: string | null;
  lastUpdate: Date | null;
  startTracking: () => Promise<boolean>;
  stopTracking: () => Promise<void>;
  tagWithLoad: (loadId: string) => Promise<void>;
  untagLoad: () => Promise<void>;
  requestPermission: () => Promise<boolean>;
}

const GPSContext = createContext<GPSContextType | undefined>(undefined);

const UPDATE_INTERVAL = 5 * 60 * 1000; // 5 minutes
const STORAGE_KEY = 'gps_tracking_state';

interface GPSProviderProps {
  children: ReactNode;
}

export function GPSProvider({ children }: GPSProviderProps) {
  const { user } = useAuth();
  const [isTracking, setIsTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [currentLoadId, setCurrentLoadId] = useState<string | null>(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const watchIdRef = useRef<number | null>(null);

  const updateLocation = async (position: GeolocationPosition) => {
    if (!user) return;

    try {
      const locationData: LocationData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        heading: position.coords.heading || 0,
        speed: position.coords.speed ? position.coords.speed * 2.237 : 0,
      };

      setCurrentLocation(locationData);

      const { error: dbError } = await supabase
        .from('driver_locations')
        .insert({
          user_id: user.id,
          load_id: currentLoadId,
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          accuracy: locationData.accuracy,
          heading: locationData.heading,
          speed: locationData.speed,
          last_updated: new Date().toISOString(),
          is_sharing_location: true,
          is_global_tracking: currentLoadId === null,
        });

      if (dbError) throw dbError;

      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Error updating location:', err);
      setError('Failed to update location. Will retry...');
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        updateLocation(position);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setError(`Unable to get location: ${err.message}`);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000,
      }
    );
  };

  const requestPermission = async (): Promise<boolean> => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return false;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => {
          setPermissionGranted(true);
          setError(null);
          resolve(true);
        },
        (err) => {
          console.error('Permission error:', err);
          if (err.code === err.PERMISSION_DENIED) {
            setError('Location permission denied. Please enable in browser settings.');
            setPermissionGranted(false);
          } else {
            setError(`Unable to get location: ${err.message}`);
          }
          resolve(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  const startTracking = async (): Promise<boolean> => {
    if (!user) {
      setError('You must be logged in to start tracking');
      return false;
    }

    if (!permissionGranted) {
      const granted = await requestPermission();
      if (!granted) return false;
    }

    setIsTracking(true);
    setError(null);

    getCurrentLocation();

    intervalRef.current = setInterval(() => {
      getCurrentLocation();
    }, UPDATE_INTERVAL);

    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      isTracking: true,
      loadId: currentLoadId,
      startedAt: new Date().toISOString(),
    }));

    return true;
  };

  const stopTracking = async () => {
    setIsTracking(false);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    if (user) {
      try {
        await supabase
          .from('driver_locations')
          .update({ is_sharing_location: false })
          .eq('user_id', user.id)
          .eq('is_global_tracking', true);
      } catch (err) {
        console.error('Error stopping tracking:', err);
      }
    }

    localStorage.removeItem(STORAGE_KEY);
  };

  const tagWithLoad = async (loadId: string) => {
    setCurrentLoadId(loadId);

    if (isTracking && user) {
      try {
        await supabase
          .from('driver_locations')
          .update({
            load_id: loadId,
            is_global_tracking: false,
          })
          .eq('user_id', user.id)
          .eq('is_global_tracking', true);

        const savedState = localStorage.getItem(STORAGE_KEY);
        if (savedState) {
          const state = JSON.parse(savedState);
          state.loadId = loadId;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        }
      } catch (err) {
        console.error('Error tagging with load:', err);
      }
    }
  };

  const untagLoad = async () => {
    const previousLoadId = currentLoadId;
    setCurrentLoadId(null);

    if (isTracking && user && previousLoadId) {
      try {
        await supabase
          .from('driver_locations')
          .update({
            load_id: null,
            is_global_tracking: true,
          })
          .eq('user_id', user.id)
          .eq('load_id', previousLoadId);

        const savedState = localStorage.getItem(STORAGE_KEY);
        if (savedState) {
          const state = JSON.parse(savedState);
          state.loadId = null;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        }
      } catch (err) {
        console.error('Error untagging load:', err);
      }
    }
  };

  useEffect(() => {
    if (user) {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          if (state.isTracking) {
            setCurrentLoadId(state.loadId);
            startTracking();
          }
        } catch (err) {
          console.error('Error restoring GPS state:', err);
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [user]);

  return (
    <GPSContext.Provider
      value={{
        isTracking,
        currentLocation,
        currentLoadId,
        permissionGranted,
        error,
        lastUpdate,
        startTracking,
        stopTracking,
        tagWithLoad,
        untagLoad,
        requestPermission,
      }}
    >
      {children}
    </GPSContext.Provider>
  );
}

export function useGPS() {
  const context = useContext(GPSContext);
  if (context === undefined) {
    throw new Error('useGPS must be used within a GPSProvider');
  }
  return context;
}
